/**
 * leaveGuard.ts — "you have unsaved edits" protection.
 *
 * Two layers, because the browser only lets us do so much:
 *  1. In-page navigation (clicking the site nav, logo, any <a> to another page) is
 *     intercepted in the capture phase — we cancel it, show the custom "Before you
 *     leave" modal, and only navigate once the user chooses. This is the common case.
 *  2. A real tab close / reload / typed URL can NOT show custom UI (browser policy);
 *     all we can do is arm `beforeunload` so the browser shows its own generic
 *     "Leave site?" dialog. If they confirm, edits are lost — by design.
 *
 * Both are gated on `isDirty()`, so a clean (saved / empty) build never nags.
 */

export interface LeaveGuardOpts {
  /** True when there are unsaved changes worth prompting about. */
  isDirty: () => boolean;
  /** Show the prompt; resolve true to proceed with leaving, false to stay. */
  onLeaveIntent: () => Promise<boolean>;
}

let installed = false;

export function installLeaveGuard(opts: LeaveGuardOpts): void {
  if (installed) return;       // one guard per page
  installed = true;

  let prompting = false;       // a modal is currently open
  let suppressUnload = false;  // we're navigating on purpose → skip the native dialog

  // Layer 2: hard exits (close / reload / external). Generic browser dialog only.
  window.addEventListener('beforeunload', e => {
    if (suppressUnload || !opts.isDirty()) return;
    e.preventDefault();
    e.returnValue = ''; // required for the dialog to show in Chrome/Edge
  });

  // Layer 1: same-tab navigation via links → our custom modal.
  document.addEventListener('click', e => {
    if (prompting || !opts.isDirty()) return;
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const anchor = (e.target as HTMLElement).closest('a');
    if (!anchor) return;
    const rawHref = anchor.getAttribute('href');
    if (!rawHref || rawHref.startsWith('#')) return;             // no-op / same-page anchor
    if (anchor.target && anchor.target !== '_self') return;       // opens a new tab/window
    if (anchor.hasAttribute('download')) return;

    let url: URL;
    try { url = new URL(anchor.href, location.href); } catch { return; }
    if (!/^https?:$/.test(url.protocol)) return;                  // mailto:, tel:, etc.
    // The cart icon links to /cart but the theme opens a drawer (no real navigation),
    // so it shouldn't trigger the save prompt.
    if (/\/cart\/?$/.test(url.pathname)) return;
    // Same document (only a hash differs) → not actually leaving.
    if (url.origin === location.origin && url.pathname === location.pathname && url.search === location.search) return;

    e.preventDefault();
    e.stopPropagation();
    prompting = true;
    opts.onLeaveIntent().then(proceed => {
      prompting = false;
      if (proceed) {
        suppressUnload = true;       // don't double-prompt with the native dialog
        window.location.href = url.href;
      }
    });
  }, true); // capture: beat the theme's own link handlers
}
